import { useEffect } from 'react';
import { useLocation } from 'react-router';
import { siteTitle } from '../../config/site';

const ROLE_TITLES: Array<[RegExp, string]> = [
  [/^\/contact\/?$/, 'Contacto'],
  [/^\/dirige-una-partida\/?$/, 'Dirige una partida'],
  // La Taberna, La Posada y las aplicaciones (dados, playgrounds…) se
  // mudaron a zona.rolsensorial.com y app.rolsensorial.com: estas rutas solo
  // redirigen (ver `routes.tsx`), así que el título es el mismo para todas
  // mientras dura ese instante.
  [/^\/(chat|taberna|posada)(\/.*)?\/?$/, 'Te llevamos a La Zona'],
  [/^\/(apps|app|playgrounds)(\/.*)?\/?$/, 'Te llevamos a las aplicaciones'],
  // El panel personal, mis actividades, reportar un error y las
  // notificaciones se mudaron a panel.rolsensorial.com: estas rutas solo
  // redirigen (ver `routes.tsx`).
  [/^\/(notifications|panel)(\/.*)?\/?$/, 'Te llevamos al panel'],
  [/^\/messages\/?$/, 'Mensajes'],
  [/^\/messages\/[^/]+\/?$/, 'Conversación privada'],
  [/^\/users\/[^/]+\/?$/, 'Perfil'],
  [/^\/settings\/?$/, 'Configuración'],
  [/^\/activities\/?$/, 'Actividades'],
  [/^\/activities\/[^/]+\/?$/, 'Actividad'],
  [/^\/board\/[^/]+\/?$/, 'Tablón'],
  [/^\/post\/[^/]+\/[^/]+\/?$/, 'Publicación'],
  [/^\/resources\/?$/, 'Recursos'],
  [/^\/register\/?$/, 'Registro'],
  [/^\/forgot-password\/?$/, 'Recuperar contraseña'],
  [/^\/admin\/activity\/?$/, 'Actividad'],
  [/^\/admin\/chats\/?$/, 'Gestión de mesas'],
  [/^\/admin\/users\/?$/, 'Cuentas'],
  [/^\/admin\/contacts\/?$/, 'Contactos'],
  [/^\/admin\/bug-reports\/?$/, 'Reportes de errores'],
  [/^\/admin\/notify\/?$/, 'Enviar notificaciones'],
  // Sistema: infraestructura, separado de Gestión (`/admin`) — las 3 URLs
  // viejas (`/admin/system`, etc.) ahora redirigen acá, ver `routes.tsx`.
  [/^\/system\/?$/, 'Sistema'],
  [/^\/system\/status\/?$/, 'Estado del sistema'],
  [/^\/system\/rungo\/?$/, 'Rungo'],
  [/^\/system\/roomit\/?$/, 'Roomit'],
  [/^\/system\/events\/?$/, 'Eventos y sonidos'],
  [/^\/system\/integrations\/?$/, 'Integraciones'],
  [/^\/system\/configuration\/?$/, 'Configuración del MVP'],
  [/^\/admin\/boards\/[^/]+\/?$/, 'Gestionar tablón'],
  [/^\/admin\/boards\/?$/, 'Gestionar tablones'],
  [/^\/admin\/posts\/new\/?$/, 'Redactar publicación'],
  [/^\/admin\/posts\/[^/]+\/edit\/?$/, 'Editar publicación'],
  [/^\/admin\/posts\/[^/]+\/?$/, 'Gestionar publicación'],
  [/^\/admin\/posts\/?$/, 'Gestionar publicaciones'],
  [/^\/admin\/?$/, 'Administración'],
];

export function pageTitle(pathname: string): string | undefined {
  if (pathname === '/') return undefined;

  return ROLE_TITLES.find(([pattern]) => pattern.test(pathname))?.[1] ?? 'Página no encontrada';
}

/** Mantiene el título de la pestaña sincronizado con la navegación cliente. */
export function DocumentTitle() {
  const { pathname } = useLocation();
  useEffect(() => {
    document.title = siteTitle(pageTitle(pathname));
  }, [pathname]);
  return null;
}

/**
 * Para una pantalla que no sigue el título de su ruta — un estado de error
 * que tapa el contenido normal, por ejemplo. Sin esto, `DocumentTitle` deja
 * el título de la ruta de siempre puesto aunque lo que se ve sea otra cosa.
 *
 * `title` en `undefined` no hace nada (la ruta sigue mandando); al dejar de
 * pasar un título, se restaura el que había antes de tomar el control.
 */
export function useOverrideDocumentTitle(title: string | undefined): void {
  useEffect(() => {
    if (!title) return undefined;
    const previous = document.title;
    document.title = siteTitle(title);
    return () => {
      document.title = previous;
    };
  }, [title]);
}
